import React, { useState, useEffect } from 'react';
import { DashboardLayout } from '../components/DashboardLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Settings as SettingsIcon,
  User,
  Bell,
  Shield,
  Database,
  Save,
  CheckCircle,
  Loader2,
  Download,
} from 'lucide-react';
import { toast } from 'sonner';
import profileService from '../services/profileService';
import complianceService from '../services/complianceService';

export default function SettingsPage() {
  const [activeTab, setActiveTab] = useState('profile');
  const [isLoadingProfile, setIsLoadingProfile] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isSavingLicense, setIsSavingLicense] = useState(false);
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [isChangePasswordDialogOpen, setIsChangePasswordDialogOpen] = useState(false);
  const [licenseData, setLicenseData] = useState(null);
  const [licenseForm, setLicenseForm] = useState({
    licenseNumber: '',
    licenseType: '',
    issueDate: '',
    expirationDate: '',
  });

  const [profileSettings, setProfileSettings] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phoneNo: '',
    organizationName: '',
  });

  const [generalSettings, setGeneralSettings] = useState({
    timezone: 'America/Los_Angeles',
    currency: 'USD',
    language: 'en',
  });

  const [notificationSettings, setNotificationSettings] = useState({
    emailNotifications: true,
    licenseExpiry: true,
    inventoryAlerts: true,
    complianceReports: true,
    systemUpdates: false,
    weeklyDigest: true,
  });

  const [securitySettings, setSecuritySettings] = useState({
    twoFactorAuth: false,
    sessionTimeout: '30',
    passwordExpiry: '90',
    ipWhitelist: '',
  });

  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });

  useEffect(() => {
    fetchProfile();
    fetchLicense();
  }, []);

  const fetchProfile = async () => {
    setIsLoadingProfile(true);
    try {
      const data = await profileService.getProfile();
      if (data) {
        setProfileSettings({
          firstName: data.firstName ?? '',
          lastName: data.lastName ?? '',
          email: data.email ?? '',
          phoneNo: data.phoneNo ?? data.phoneNumber ?? '',
          organizationName: data.organizationName ?? '',
        });
      }
    } catch (error) {
      console.error('Failed to load profile:', error);
    } finally {
      setIsLoadingProfile(false);
    }
  };

  const fetchLicense = async () => {
    try {
      const data = await complianceService.getLicenses();
      const licenses = Array.isArray(data) ? data : [];
      if (licenses.length > 0) {
        const primary = licenses[0];
        setLicenseData(primary);
        setLicenseForm({
          licenseNumber: primary.licenseNumber ?? '',
          licenseType: primary.licenseType ?? '',
          issueDate: primary.issueDate?.split('T')[0] ?? '',
          expirationDate: primary.expirationDate?.split('T')[0] ?? '',
        });
      }
    } catch (error) {
      console.error('Failed to load license:', error);
    }
  };

  const handleSaveLicense = async () => {
    if (!licenseData?.id) return;
    setIsSavingLicense(true);
    try {
      await complianceService.updateLicense(licenseData.id, licenseForm);
      toast.success('License updated successfully');
      fetchLicense();
    } catch (error) {
      toast.error(typeof error === 'string' ? error : 'Failed to update license');
    } finally {
      setIsSavingLicense(false);
    }
  };

  const handleSaveProfile = async () => {
    setIsSaving(true);
    try {
      await profileService.updateProfile({
        firstName: profileSettings.firstName,
        lastName: profileSettings.lastName,
        phoneNo: profileSettings.phoneNo,
        organizationName: profileSettings.organizationName,
      });
      toast.success('Profile updated successfully');
    } catch (error) {
      toast.error(typeof error === 'string' ? error : 'Failed to update profile');
    } finally {
      setIsSaving(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      toast.error('New passwords do not match');
      return;
    }
    if (passwordForm.newPassword.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    setIsChangingPassword(true);
    try {
      await profileService.changePassword({
        currentPassword: passwordForm.currentPassword,
        newPassword: passwordForm.newPassword,
      });
      toast.success('Password changed successfully');
      setIsChangePasswordDialogOpen(false);
      setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
    } catch (error) {
      toast.error(typeof error === 'string' ? error : 'Failed to change password');
    } finally {
      setIsChangingPassword(false);
    }
  };

  const tabs = [
    { id: 'profile', label: 'Profile', icon: User },
    { id: 'general', label: 'General', icon: SettingsIcon },
    { id: 'notifications', label: 'Notifications', icon: Bell },
    { id: 'security', label: 'Security', icon: Shield },
    { id: 'data', label: 'Data & Privacy', icon: Database },
  ];

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-3xl font-bold text-foreground">Settings</h1>
          <p className="text-muted-foreground mt-1">Manage your system configuration and preferences</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Sidebar */}
          <div className="lg:col-span-1">
            <Card>
              <CardContent className="p-4">
                <nav className="space-y-1">
                  {tabs.map((tab) => (
                    <button
                      key={tab.id}
                      onClick={() => setActiveTab(tab.id)}
                      className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                        activeTab === tab.id
                          ? 'bg-primary text-primary-foreground'
                          : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
                      }`}
                    >
                      <tab.icon className="h-4 w-4" />
                      {tab.label}
                    </button>
                  ))}
                </nav>
              </CardContent>
            </Card>
          </div>

          {/* Content */}
          <div className="lg:col-span-3">

            {/* Profile Settings */}
            {activeTab === 'profile' && (
              <Card>
                <CardHeader>
                  <CardTitle>Profile Settings</CardTitle>
                  <CardDescription>Manage your personal information</CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  {isLoadingProfile ? (
                    <div className="flex items-center justify-center py-8">
                      <Loader2 className="h-8 w-8 animate-spin text-primary" />
                    </div>
                  ) : (
                    <div className="space-y-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor="firstName">First Name</Label>
                          <Input
                            id="firstName"
                            value={profileSettings.firstName}
                            onChange={(e) => setProfileSettings({ ...profileSettings, firstName: e.target.value })}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="lastName">Last Name</Label>
                          <Input
                            id="lastName"
                            value={profileSettings.lastName}
                            onChange={(e) => setProfileSettings({ ...profileSettings, lastName: e.target.value })}
                          />
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="email">Email Address</Label>
                        <Input id="email" type="email" value={profileSettings.email} disabled className="bg-muted" />
                        <p className="text-xs text-muted-foreground">Email address cannot be changed</p>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="phoneNo">Phone Number</Label>
                        <Input
                          id="phoneNo"
                          type="tel"
                          value={profileSettings.phoneNo}
                          onChange={(e) => setProfileSettings({ ...profileSettings, phoneNo: e.target.value })}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="organizationName">Organization Name</Label>
                        <Input
                          id="organizationName"
                          value={profileSettings.organizationName}
                          onChange={(e) => setProfileSettings({ ...profileSettings, organizationName: e.target.value })}
                        />
                      </div>
                    </div>
                  )}
                  {/* License Information */}
                  <div className="pt-4 border-t space-y-4">
                    <div>
                      <h3 className="text-sm font-semibold text-foreground">License Information</h3>
                      <p className="text-xs text-muted-foreground mt-0.5">Update your primary operator license details</p>
                    </div>
                    {licenseData ? (
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor="licenseNumber">License Number</Label>
                          <Input
                            id="licenseNumber"
                            value={licenseForm.licenseNumber}
                            onChange={(e) => setLicenseForm(p => ({ ...p, licenseNumber: e.target.value }))}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="licenseType">License Type</Label>
                          <Input
                            id="licenseType"
                            value={licenseForm.licenseType}
                            onChange={(e) => setLicenseForm(p => ({ ...p, licenseType: e.target.value }))}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="issueDate">Issue Date</Label>
                          <Input
                            id="issueDate"
                            type="date"
                            value={licenseForm.issueDate}
                            onChange={(e) => setLicenseForm(p => ({ ...p, issueDate: e.target.value }))}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="expirationDate">Expiry Date</Label>
                          <Input
                            id="expirationDate"
                            type="date"
                            value={licenseForm.expirationDate}
                            onChange={(e) => setLicenseForm(p => ({ ...p, expirationDate: e.target.value }))}
                          />
                        </div>
                      </div>
                    ) : (
                      <p className="text-sm text-muted-foreground">No license on file. Add a license from the License Management page.</p>
                    )}
                    {licenseData && (
                      <div className="flex justify-end">
                        <Button onClick={handleSaveLicense} disabled={isSavingLicense} size="sm">
                          {isSavingLicense ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
                          Save License
                        </Button>
                      </div>
                    )}
                  </div>

                  <div className="flex justify-end gap-2">
                    <Button variant="outline" onClick={() => setIsChangePasswordDialogOpen(true)}>
                      Change Password
                    </Button>
                    <Button onClick={handleSaveProfile} disabled={isSaving || isLoadingProfile}>
                      {isSaving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
                      Save Changes
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* General Settings */}
            {activeTab === 'general' && (
              <Card>
                <CardHeader>
                  <CardTitle>General Settings</CardTitle>
                  <CardDescription>Configure your basic system preferences</CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Timezone</Label>
                        <Select value={generalSettings.timezone} onValueChange={(v) => setGeneralSettings({ ...generalSettings, timezone: v })}>
                          <SelectTrigger><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="America/Los_Angeles">Pacific Time</SelectItem>
                            <SelectItem value="America/Denver">Mountain Time</SelectItem>
                            <SelectItem value="America/Chicago">Central Time</SelectItem>
                            <SelectItem value="America/New_York">Eastern Time</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label>Currency</Label>
                        <Select value={generalSettings.currency} onValueChange={(v) => setGeneralSettings({ ...generalSettings, currency: v })}>
                          <SelectTrigger><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="USD">USD ($)</SelectItem>
                            <SelectItem value="CAD">CAD ($)</SelectItem>
                            <SelectItem value="EUR">EUR (€)</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label>Language</Label>
                      <Select value={generalSettings.language} onValueChange={(v) => setGeneralSettings({ ...generalSettings, language: v })}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="en">English</SelectItem>
                          <SelectItem value="es">Spanish</SelectItem>
                          <SelectItem value="fr">French</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="flex justify-end">
                    <Button onClick={() => toast.success('General settings saved')}>
                      <Save className="h-4 w-4 mr-2" />
                      Save Changes
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Notification Settings */}
            {activeTab === 'notifications' && (
              <Card>
                <CardHeader>
                  <CardTitle>Notification Preferences</CardTitle>
                  <CardDescription>Configure how you receive notifications</CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="space-y-4">
                    {[
                      { key: 'emailNotifications', label: 'Email Notifications', desc: 'Receive notifications via email' },
                      { key: 'licenseExpiry', label: 'License Expiry Alerts', desc: 'Get notified when licenses are expiring' },
                      { key: 'inventoryAlerts', label: 'Inventory Alerts', desc: 'Low stock and critical inventory warnings' },
                      { key: 'complianceReports', label: 'Compliance Reports', desc: 'Report submission and approval notifications' },
                      { key: 'systemUpdates', label: 'System Updates', desc: 'Platform updates and new features' },
                      { key: 'weeklyDigest', label: 'Weekly Digest', desc: 'Receive weekly summary of activities' },
                    ].map(({ key, label, desc }) => (
                      <div key={key} className="flex items-center justify-between p-4 border rounded-lg">
                        <div className="space-y-0.5">
                          <Label>{label}</Label>
                          <p className="text-sm text-muted-foreground">{desc}</p>
                        </div>
                        <Button
                          variant={notificationSettings[key] ? 'default' : 'outline'}
                          size="sm"
                          onClick={() => setNotificationSettings(p => ({ ...p, [key]: !p[key] }))}
                        >
                          {notificationSettings[key] ? 'Enabled' : 'Disabled'}
                        </Button>
                      </div>
                    ))}
                  </div>
                  <div className="flex justify-end">
                    <Button onClick={() => toast.success('Notification preferences saved')}>
                      <Save className="h-4 w-4 mr-2" />
                      Save Preferences
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Security Settings */}
            {activeTab === 'security' && (
              <Card>
                <CardHeader>
                  <CardTitle>Security Settings</CardTitle>
                  <CardDescription>Manage your account security and access controls</CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="space-y-4">
                    <div className="flex items-center justify-between p-4 border rounded-lg">
                      <div className="space-y-0.5">
                        <Label>Two-Factor Authentication</Label>
                        <p className="text-sm text-muted-foreground">Add an extra layer of security to your account</p>
                      </div>
                      <Button
                        variant={securitySettings.twoFactorAuth ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => setSecuritySettings(p => ({ ...p, twoFactorAuth: !p.twoFactorAuth }))}
                      >
                        {securitySettings.twoFactorAuth ? 'Enabled' : 'Disabled'}
                      </Button>
                    </div>
                    <div className="space-y-2">
                      <Label>Session Timeout (minutes)</Label>
                      <Select value={securitySettings.sessionTimeout} onValueChange={(v) => setSecuritySettings(p => ({ ...p, sessionTimeout: v }))}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="15">15 minutes</SelectItem>
                          <SelectItem value="30">30 minutes</SelectItem>
                          <SelectItem value="60">1 hour</SelectItem>
                          <SelectItem value="120">2 hours</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Password Expiry (days)</Label>
                      <Select value={securitySettings.passwordExpiry} onValueChange={(v) => setSecuritySettings(p => ({ ...p, passwordExpiry: v }))}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="30">30 days</SelectItem>
                          <SelectItem value="60">60 days</SelectItem>
                          <SelectItem value="90">90 days</SelectItem>
                          <SelectItem value="never">Never</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>IP Whitelist</Label>
                      <Textarea
                        placeholder="Enter IP addresses (one per line)"
                        value={securitySettings.ipWhitelist}
                        onChange={(e) => setSecuritySettings(p => ({ ...p, ipWhitelist: e.target.value }))}
                        rows={4}
                      />
                      <p className="text-xs text-muted-foreground">Restrict access to specific IP addresses</p>
                    </div>
                  </div>
                  <div className="flex justify-end">
                    <Button onClick={() => toast.success('Security settings saved')}>
                      <Save className="h-4 w-4 mr-2" />
                      Save Changes
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Data & Privacy */}
            {activeTab === 'data' && (
              <Card>
                <CardHeader>
                  <CardTitle>Data & Privacy</CardTitle>
                  <CardDescription>Control your data and privacy settings</CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="space-y-4">
                    <div className="p-4 border rounded-lg">
                      <div className="flex items-start gap-3">
                        <CheckCircle className="h-5 w-5 text-green-600 mt-0.5" />
                        <div>
                          <h4 className="font-medium">Data Encryption</h4>
                          <p className="text-sm text-muted-foreground mt-1">
                            All your data is encrypted at rest and in transit using industry-standard encryption protocols.
                          </p>
                        </div>
                      </div>
                    </div>
                    <div className="p-4 border rounded-lg">
                      <h4 className="font-medium mb-3">Export Your Data</h4>
                      <p className="text-sm text-muted-foreground mb-3">Download a complete copy of your data in JSON format</p>
                      <Button variant="outline">
                        <Download className="h-4 w-4 mr-2" />
                        Request Data Export
                      </Button>
                    </div>
                    <div className="p-4 border rounded-lg border-red-200 bg-red-50">
                      <h4 className="font-medium text-red-900 mb-2">Danger Zone</h4>
                      <p className="text-sm text-red-700 mb-3">
                        Permanently delete your account and all associated data. This action cannot be undone.
                      </p>
                      <Button variant="destructive">Delete Account</Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>

      {/* Change Password Dialog */}
      <Dialog open={isChangePasswordDialogOpen} onOpenChange={setIsChangePasswordDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Change Password</DialogTitle>
            <DialogDescription>Enter your current password and choose a new one</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleChangePassword} className="space-y-4">
            <div className="space-y-2">
              <Label>Current Password</Label>
              <Input type="password" required value={passwordForm.currentPassword} onChange={(e) => setPasswordForm(p => ({ ...p, currentPassword: e.target.value }))} />
            </div>
            <div className="space-y-2">
              <Label>New Password</Label>
              <Input type="password" required placeholder="At least 8 characters" value={passwordForm.newPassword} onChange={(e) => setPasswordForm(p => ({ ...p, newPassword: e.target.value }))} />
            </div>
            <div className="space-y-2">
              <Label>Confirm New Password</Label>
              <Input type="password" required value={passwordForm.confirmPassword} onChange={(e) => setPasswordForm(p => ({ ...p, confirmPassword: e.target.value }))} />
            </div>
            <div className="flex gap-2">
              <Button type="submit" className="flex-1" disabled={isChangingPassword}>
                {isChangingPassword && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                Change Password
              </Button>
              <Button type="button" variant="outline" className="flex-1" onClick={() => setIsChangePasswordDialogOpen(false)}>
                Cancel
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
